/**
 * Display formatting for the board: ages, byte sizes, timestamps, durations.
 * Pure functions so the unit tests can pin them (and the pages stay dumb).
 */

/** 20297745855 -> "20.3 GB"; null -> "—". */
export function formatBytes(bytes: number | null | undefined): string {
  if (bytes === null || bytes === undefined || !Number.isFinite(bytes)) return '—';
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let value = bytes;
  let unit = 'B';
  for (const next of units) {
    if (value < 1024) break;
    value /= 1024;
    unit = next;
  }
  return `${value >= 100 ? Math.round(value) : value.toFixed(1)} ${unit}`;
}

/** 7265 -> "2 ч 1 мин"; null -> "—". Compact, no seconds below an hour. */
export function formatAge(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const value = Math.max(0, Math.round(seconds));
  if (value < 60) return `${value} с`;
  const minutes = Math.floor(value / 60);
  if (minutes < 60) return `${minutes} мин`;
  const hours = Math.floor(minutes / 60);
  if (hours < 48) return `${hours} ч ${minutes % 60} мин`;
  return `${Math.floor(hours / 24)} д ${hours % 24} ч`;
}

/** 3600 -> "1:00:00"; 1720 -> "28:40"; null -> "—". */
export function formatDuration(seconds: number | null | undefined): string {
  if (seconds === null || seconds === undefined || !Number.isFinite(seconds)) return '—';
  const total = Math.max(0, Math.round(seconds));
  const h = Math.floor(total / 3600);
  const m = Math.floor((total % 3600) / 60);
  const s = total % 60;
  const pad = (n: number): string => String(n).padStart(2, '0');
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** ISO ts -> local "09.10 01:00"; null -> "—". */
export function formatTs(iso: string | number | null | undefined): string {
  if (iso === null || iso === undefined || iso === '') return '—';
  const date = typeof iso === 'number' ? new Date(iso) : new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

/** ISO ts -> "09.10 01:00:42" (the timeline needs seconds). */
export function formatTsFull(iso: string | null | undefined): string {
  if (!iso) return '—';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return '—';
  const pad = (n: number): string => String(n).padStart(2, '0');
  return `${pad(date.getDate())}.${pad(date.getMonth() + 1)} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`;
}

/** `s3://bucket/a/b/2026-10-08.dump` -> `…/2026-10-08.dump` keeps the tail visible. */
export function shortenPath(path: string | null, max = 42): string {
  if (!path) return '—';
  if (path.length <= max) return path;
  return `…${path.slice(-(max - 1))}`;
}

/** sha256 -> the first 10 hex chars (the full value rides the title). */
export function shortSha(sha: string | null): string {
  return sha ? sha.slice(0, 10) : '—';
}
